import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Stack,
} from '@mui/material';

interface SubscriptionInfo {
  subscription_id: string;
  product_name: string;
  access_level: string;
  start_date: string;
  end_date?: string;
  auto_renew: boolean;
  cost: number;
  renewal_cycle: string;
}

interface ReactivateSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  subscriptionInfo: SubscriptionInfo;
}

const ReactivateSubscriptionDialog: React.FC<ReactivateSubscriptionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  subscriptionInfo,
}) => {
  const [reactivatingSubscription, setReactivatingSubscription] = React.useState(false);

  const formatEndDate = (endDate: string | undefined) => {
    if (!endDate) return 'now';
    return new Date(endDate).toLocaleDateString();
  };

  const handleReactivateSubscription = async () => {
    setReactivatingSubscription(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
    } finally {
      setReactivatingSubscription(false);
    }
  };

  const handleClose = () => {
    if (!reactivatingSubscription) {
      onClose();
    }
  };

  // Reset loading state when dialog closes
  React.useEffect(() => {
    if (!open) {
      setReactivatingSubscription(false);
    }
  }, [open]);


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Reactivate subscription
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            Reactivate your subscription to keep access to your {subscriptionInfo.product_name} benefits. 
            Once reactivated, you will continue to be billed each {subscriptionInfo.renewal_cycle}, starting {formatEndDate(subscriptionInfo.end_date)}.
          </DialogContentText>
          <DialogContentText>
            Do you want to reactivate your subscription?
          </DialogContentText>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" disabled={reactivatingSubscription}>
          No
        </Button>
        <Button 
          onClick={handleReactivateSubscription} 
          color="primary" 
          variant="contained"
          disabled={reactivatingSubscription}
        >
          {reactivatingSubscription ? 'Reactivating...' : 'Yes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReactivateSubscriptionDialog;
